import { Suspense } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import type { decideTab_GroupQuery } from "@app/queries/decideTab_GroupQuery.graphql";

import { CandidatePicker, VERDICT_HINTS, VerbButton, VERBS, cents } from "./shared";

export type Group = {
  readonly counterparty: string;
  readonly count: number;
  readonly sumCents: number;
  readonly withCandidates: number;
  readonly matchable: number;
  readonly counted: number;
  readonly countedSumCents: number;
  readonly uncounted: number;
  readonly uncountedSumCents: number;
  readonly shape?: string | null;
  readonly recommendedAction?: string | null;
};

const SHAPES: Record<string, { label: string; hint: string; tone: string }> = {
  FEE_TRICKLE: {
    label: "fee trickle",
    hint: "Many small records (cents to a few dollars each) with no bank or wallet row nearby. Wallets and banks book their own fees this way, so these are almost always real costs QuickBooks recorded but the statements never itemised.",
    tone: "bg-emerald-50 text-emerald-700",
  },
  NEAR_TWIN_CLUSTER: {
    label: "near-twin cluster",
    hint: "Most of these records have a bank or wallet row within three days, under the same payee, within $50 or 2% — the payment QuickBooks booked at the invoice amount and the wallet settled minus fees and FX. Left alone, each one counts TWICE: once as the bank row and once as the QuickBooks copy.",
    tone: "bg-indigo-50 text-indigo-700",
  },
  NO_EVIDENCE: {
    label: "no evidence",
    hint: "No bank or wallet row is anywhere near these records. Either QuickBooks knows about money the statements do not (a real cost you should keep) or the entry was a mistake, a duplicate, or a non-cash booking (exclude it).",
    tone: "bg-gray-100 text-gray-600",
  },
};

// What each verb does to THIS group, in its own numbers — the page must never
// leave a person guessing what a click moves in the books.
function Consequences({ group }: { group: Group }) {
  const shape = group.shape ? SHAPES[group.shape] : null;
  return (
    <div className="space-y-1 border-t bg-gray-50 px-4 py-2 text-xs text-gray-600">
      {shape && <p className="text-gray-700">{shape.hint}</p>}
      <p>
        <span className="font-medium text-gray-800">Today:</span> {group.counted.toLocaleString()} of these records are counted in your
        books ({cents(group.countedSumCents)}); {group.uncounted.toLocaleString()} are not ({cents(group.uncountedSumCents)}).
      </p>
      <p>
        <span className="font-medium text-emerald-700">{VERBS.CONFIRM.label} all</span> — treats every record as a real cost:{" "}
        {group.uncounted > 0
          ? `the ${group.uncounted.toLocaleString()} not-counted ones enter your books (${cents(group.uncountedSumCents)}); `
          : "nothing new enters your books; "}
        the counted ones stay. Undo brings them back here.
      </p>
      <p>
        <span className="font-medium text-red-700">{VERBS.DISMISS.label} all</span> — treats every record as noise:{" "}
        {group.counted > 0
          ? `the ${group.counted.toLocaleString()} counted ones leave your books (${cents(group.countedSumCents)}); `
          : "nothing leaves your books; "}
        the rest just drop off this list. QuickBooks is not changed. Undo brings them back here.
      </p>
      <p>
        <span className="font-medium text-indigo-700">{VERBS.BIND.label}</span> — says a record IS a bank or wallet row: the bank row stays as
        the single line in your books, the QuickBooks copy disappears, and the difference is recorded as fee/FX drift.
        {group.matchable > 0 && ` ${group.matchable.toLocaleString()} record${group.matchable === 1 ? " has" : "s have"} exactly one such row and can be matched in one click; the others need you to pick.`}
        {" "}Undo from the Auto-matched tab.
      </p>
    </div>
  );
}

// The suggestion is a button, not a label: it runs the verb the shape calls for.
// A near-twin cluster's verb is "Match N with one twin" — every record with
// exactly one payee-matching twin nobody else claims, bound in one background
// run; the ambiguous rest stays in the card's records for the picker.
function Suggestion({ group, busy, onAct }: { group: Group; busy: boolean; onAct: (verb: "CONFIRM" | "DISMISS" | "MATCH") => void }) {
  const base = "rounded px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50";
  switch (group.recommendedAction) {
    case "CONFIRM":
      return (
        <button type="button" disabled={busy} onClick={() => onAct("CONFIRM")} title={VERBS.CONFIRM.consequence} className={`${base} bg-emerald-600 hover:bg-emerald-700`}>
          Suggested: {VERBS.CONFIRM.label} all {group.count.toLocaleString()}
        </button>
      );
    case "DISMISS":
      return (
        <button type="button" disabled={busy} onClick={() => onAct("DISMISS")} title={VERBS.DISMISS.consequence} className={`${base} bg-red-600 hover:bg-red-700`}>
          Suggested: {VERBS.DISMISS.label} all {group.count.toLocaleString()}
        </button>
      );
    case "BIND":
      return group.matchable > 0 ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => onAct("MATCH")}
          title="Bind every record that has exactly one bank or wallet twin under this payee; records with several twins stay here for you to pick"
          className={`${base} bg-indigo-600 hover:bg-indigo-700`}
        >
          Suggested: {VERBS.BIND.label} {group.matchable.toLocaleString()} with one twin
        </button>
      ) : (
        <span className="text-xs text-indigo-700">Suggested: pick each record's twin below</span>
      );
    default:
      return null;
  }
}

// The group's records, loaded on expand under their own cap — a card opened on
// a 1,886-record queue would find few of its rows in the page's 200 newest.
const groupQuery = graphql`
  query decideTab_GroupQuery($counterparty: String!) {
    sourcingReview(counterparty: $counterparty) {
      items {
        qboId
        entityType
        verdict
        amountCents
        txnDate
        candidates {
          txn {
            id
            amount
            postingDate
            description
          }
          exact
          deltaCents
          payee
          rivalCounterparty
          rivalDate
        }
      }
    }
  }
`;

function GroupRecords({
  counterparty,
  fetchKey,
  busy,
  onBind,
}: {
  counterparty: string;
  fetchKey: number;
  busy: boolean;
  onBind: (item: { qboId: string; entityType: string }, bankTxnId: string) => void;
}) {
  const data = useLazyLoadQuery<decideTab_GroupQuery>(
    groupQuery,
    { counterparty },
    { fetchKey, fetchPolicy: fetchKey === 0 ? "store-and-network" : "network-only" },
  );
  const items = data.sourcingReview?.items ?? [];
  return (
    <ul className="divide-y">
      {items.map((item) => (
        <li key={`${item.entityType}:${item.qboId}`} className="space-y-1 py-2">
          <div className="flex items-center gap-3 text-xs text-gray-700">
            <span className="tabular-nums text-gray-500">{item.txnDate ?? "—"}</span>
            <span className="text-gray-400">
              {item.entityType} #{item.qboId}
            </span>
            <span className={`tabular-nums font-medium ${item.amountCents < 0 ? "text-red-600" : "text-green-600"}`}>
              {cents(item.amountCents)}
            </span>
            <span className="text-[11px] text-gray-400" title={VERDICT_HINTS[item.verdict] ?? ""}>
              {item.verdict.toLowerCase()} — {VERDICT_HINTS[item.verdict] ?? ""}
            </span>
          </div>
          <CandidatePicker candidates={item.candidates} busy={busy} onBind={(id) => onBind(item, id)} />
        </li>
      ))}
    </ul>
  );
}

export function DecideTab({
  groups,
  expanded,
  onExpand,
  fetchKey,
  busy,
  onGroupAction,
  onBind,
}: {
  groups: ReadonlyArray<Group>;
  expanded: string | null;
  onExpand: (counterparty: string | null) => void;
  fetchKey: number;
  busy: boolean;
  onGroupAction: (verb: "CONFIRM" | "DISMISS" | "MATCH", group: Group) => void;
  onBind: (item: { qboId: string; entityType: string }, bankTxnId: string) => void;
}) {
  if (groups.length === 0) return null;
  return (
    <ul className="space-y-2">
      {groups.map((group) => {
        const open = expanded === group.counterparty;
        const shape = group.shape ? SHAPES[group.shape] : null;
        return (
          <li key={group.counterparty || "∅"} className="rounded border bg-white">
            <div className="flex flex-wrap items-center gap-3 px-4 py-3">
              <button
                type="button"
                onClick={() => onExpand(open ? null : group.counterparty)}
                className="text-left text-sm font-medium text-gray-900 hover:underline"
              >
                {group.counterparty || <span className="text-gray-400">(no counterparty)</span>}
              </button>
              {shape && <span className={`rounded px-1.5 py-0.5 text-[11px] ${shape.tone}`}>{shape.label}</span>}
              <span className="text-xs text-gray-500">
                {group.count.toLocaleString()} record{group.count === 1 ? "" : "s"} ·{" "}
                <span className={`tabular-nums ${group.sumCents < 0 ? "text-red-600" : "text-green-600"}`}>{cents(group.sumCents)}</span>
                {" · "}
                {group.withCandidates.toLocaleString()} with a bank twin
                {group.withCandidates > 0 && ` (${group.matchable.toLocaleString()} unambiguous)`} · {group.counted.toLocaleString()} counted
              </span>
              <Suggestion group={group} busy={busy} onAct={(verb) => onGroupAction(verb, group)} />
              <div className="ml-auto flex items-center gap-2">
                <VerbButton verb="CONFIRM" disabled={busy} onClick={() => onGroupAction("CONFIRM", group)} className="px-2 py-0.5" />
                <VerbButton verb="DISMISS" disabled={busy} onClick={() => onGroupAction("DISMISS", group)} className="px-2 py-0.5" />
                <button
                  type="button"
                  onClick={() => onExpand(open ? null : group.counterparty)}
                  className="text-xs text-indigo-600 underline hover:no-underline"
                >
                  {open ? "close" : "records"}
                </button>
              </div>
            </div>
            <Consequences group={group} />
            {open && (
              <div className="border-t bg-indigo-50/40 px-4 py-2">
                <p className="mb-2 text-xs text-gray-500">
                  Each record with the bank or wallet rows it could be — exact amount first, then near twins with the difference. "different
                  payee" means the bank description shares no name with the QuickBooks counterparty; "also the only candidate for…" means
                  another record wants the same row, so pick deliberately.
                </p>
                <Suspense fallback={<p className="py-2 text-xs text-gray-400">Loading records…</p>}>
                  <GroupRecords counterparty={group.counterparty} fetchKey={fetchKey} busy={busy} onBind={onBind} />
                </Suspense>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
